import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDto } from '@api/users/interfaces';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { userPages } from '@app/pages/user/user.routes';
import { MenuService } from '@app/services/menu';
import { UserRestService } from '@app/services/rest/user-rest.service';
import * as Reducers from '@app/store/reducers';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Resolver } from '@stoplight/json-ref-resolver/resolver';
import { SessionItemData } from '@workflow/services/session-item-data';
import { JSONSchema7 } from 'json-schema';
import { combineLatest,  Subscription } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import userSchema from '../userSchema_edit.json';
import userSchemaWO from '../userSchema_edit_without_old.json';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { PermissionService } from '@app/services/permission/permission.service';
import { firstValueFrom } from '@app/util/rxjs-7-compat';

@Component({
	selector: 'app-user-edit',
	templateUrl: './user-edit.component.html',
	styleUrls: ['../user-pages.style.scss'],
})
export class UserEditComponent implements OnDestroy {
	private subscription = new Subscription();

	userFormID = 'form-edit-user';
	data: SessionItemData = null;
	resolvedSchema: JSONSchema7 = null;
	user: UserDto;
	activeUser: UserDto;
	deleteModalVisible = false;
	showWarning = false;
	showError = false;
	alertMessage: string;
	isSameUser: boolean;

	public userEditSplitButton: SplitButtonElement = {
		title: 'actions.save',
		icon: 'save',
		type: 'primary',
		buttonType: 'submit',
		attrForm: this.userFormID,
	};
	public userCancelSplitButton: SplitButtonElement = {
		title: 'actions.cancel',
		icon: 'x',
		type: 'default',
		callback: () => this.navigateAway(),
	};
	public userDeleteSplitButton: SplitButtonElement = {
		title: 'actions.delete',
		icon: 'trash',
		type: 'default',
		callback: () => this.openDeleteUserModal(),
	};
	public userRoleButton: SplitButtonElement = {
		title: 'user.projectsAndRoles',
		icon: 'plus',
		type: 'default',
		callback: () => this.navigateToRoles(),
	};

	constructor(
		private readonly userRestService: UserRestService,
		private readonly notification: NzNotificationService,
		private readonly router: Router,
		activatedRoute: ActivatedRoute,
		store: Store<never>,
		menuService: MenuService,
		translateService: TranslateService,
		private readonly permissionServices: PermissionService,
	) {
		const user$ = activatedRoute.paramMap.pipe(
			map(paramMap => paramMap.get('username')),
			switchMap(username => userRestService.userByUsername$(username)),
			tap(userDto => delete userDto.roles),
			shareReplay({ bufferSize: 1, refCount: false }),
		);

		const activeUser$ = store.pipe(
			select(Reducers.getUser),
		);

		const title$ = user$.pipe(
			switchMap(user => translateService.stream('actions.edit.item', { item: user.username })),
		);

		this.subscription.add(
			combineLatest([
				user$,
				activeUser$
			]).subscribe(([user, activeUser]) => {
				this.user = user;
				this.isSameUser = user.username === activeUser.username;
				this.activeUser = activeUser;

				this.userRoleButton = {
					...this.userRoleButton,
					disabled: this.isSameUser,
				};

				this.resolveSchema(this.isSameUser ? userSchema as JSONSchema7 : userSchemaWO as JSONSchema7).catch(error => console.error(error));
			}));
		this.subscription.add(
			user$.subscribe(
				userDto => this.data = new SessionItemData({ userData: userDto }),
			),
		);
		this.subscription.add(
			title$.subscribe(
				title => menuService.title = title,
			),
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	async editUser(isFormValid: boolean): Promise<void> {
		if (!isFormValid) {
			return;
		}

		const isUserWrite = await firstValueFrom(this.permissionServices.hasPermission$('users:write'));

		const oldPassword = this.data.data.userData.old_password;
		const password = this.data.data.userData.password;
		const passwordConfirm = this.data.data.userData.password_confirm;
		this.showWarning = false;
		this.showError = false;

		if (password !== passwordConfirm) {
			this.alertMessage = 'message.warning.password_not_match';
			this.showWarning = true;
			return;
		}
		if (password && !oldPassword && this.isSameUser) {
			this.alertMessage = 'message.warning.old_password.missing';
			this.showWarning = true;
			return;
		}
		if (!password && oldPassword && this.isSameUser) {
			this.alertMessage = 'message.warning.password.missing';
			this.showWarning = true;
			return;
		}

		try {
			const user = await this.userRestService.updateUser(this.data.data.userData as UserDto);
			if (oldPassword && password && this.isSameUser) {
				const pw = { old_password: oldPassword, password: password };
				await this.userRestService.updatePassword(pw, user.username);
			} else if (!oldPassword && password && isUserWrite) {
				const pw = { password: password };
				await this.userRestService.updatePassword(pw, user.username);
			}
			this.notification.success('', 'Benutzer ' + user.username + ' erfolgreich bearbeitet');
			this.navigateAway();
		} catch (error) {
			this.alertMessage = 'message.error.password.incorrect';
			this.showError = true;
			console.log(error);
		}
	}

	async deleteUser(): Promise<void> {
		const username = (this.data.data.userData as UserDto).username;
		try {
			await this.userRestService.deleteUser(username);
			this.notification.success('', 'Benutzer ' + username + ' erfolgreich gelöscht');
			this.navigateAway();
		} catch (error) {
			this.alertMessage = 'message.error.delete.user';
			this.showError = true;
			console.log(error);
		}
	}

	async resolveSchema(schema: JSONSchema7): Promise<JSONSchema7> {
		const resolver = new Resolver();
		const result = await resolver.resolve(schema);
		this.resolvedSchema = result.result;
		return result.result;
	}

	navigateAway(): void {
		this.router
			.navigate([userPages.dashboard.base()])
			.catch(console.error);
	}

	navigateToRoles(): void {
		this.router
			.navigate([userPages.projectRoles.base(this.user.username)])
			.catch(console.error);
	}

	openDeleteUserModal(): void {
		this.deleteModalVisible = true;
	}

	closeDeleteUserModal(): void {
		this.deleteModalVisible = false;
	}

	setSubmitActive(active: boolean): void {
		this.userEditSplitButton.disabled = !active;
	}

}
